import {
  FC,
  HTMLAttributes,
  MouseEvent,
  useState,
  useEffect,
  useMemo,
  memo,
} from 'react';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import {
  Card,
  CardContent,
  Typography,
  Avatar,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import PersonIcon from '@material-ui/icons/Person';
import FaceIcon from '@material-ui/icons/Face';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import CheckIcon from '@material-ui/icons/Check';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { autoLinkUrls } from '@internal/app-common/autoLinkUrls';
import { linkifyChildren } from '@internal/app-common/linkifyReact';
import type { ResponseEmbedding } from '@internal/plugin-rag-ai';
import { EmbeddingsView } from '@internal/plugin-rag-ai';
import type { AgentToolCallTrace } from '../../api/MgaApi';
import { MessageRole } from '../../api/constants';
import { AgentSourcesView } from './AgentSourcesView';
import { PrismAsyncLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import {
  oneLight,
  oneDark,
} from 'react-syntax-highlighter/dist/esm/styles/prism';
import tsx from 'react-syntax-highlighter/dist/esm/languages/prism/tsx';
import typescript from 'react-syntax-highlighter/dist/esm/languages/prism/typescript';
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import yaml from 'react-syntax-highlighter/dist/esm/languages/prism/yaml';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import css from 'react-syntax-highlighter/dist/esm/languages/prism/css';
import markup from 'react-syntax-highlighter/dist/esm/languages/prism/markup';
import markdown from 'react-syntax-highlighter/dist/esm/languages/prism/markdown';

let registered = false;
function registerLanguages() {
  if (registered) return;
  SyntaxHighlighter.registerLanguage('tsx', tsx);
  SyntaxHighlighter.registerLanguage('typescript', typescript);
  SyntaxHighlighter.registerLanguage('javascript', javascript);
  SyntaxHighlighter.registerLanguage('python', python);
  SyntaxHighlighter.registerLanguage('json', json);
  SyntaxHighlighter.registerLanguage('yaml', yaml);
  SyntaxHighlighter.registerLanguage('bash', bash);
  SyntaxHighlighter.registerLanguage('css', css);
  SyntaxHighlighter.registerLanguage('markup', markup);
  SyntaxHighlighter.registerLanguage('markdown', markdown);
  registered = true;
}

const LANG_ALIASES: Record<string, string> = {
  ts: 'typescript',
  tsx: 'tsx',
  typescript: 'typescript',
  js: 'javascript',
  jsx: 'javascript',
  json: 'json',
  javascript: 'javascript',
  py: 'python',
  sh: 'bash',
  shell: 'bash',
  bash: 'bash',
  yml: 'yaml',
  html: 'markup',
  xml: 'markup',
  md: 'markdown',
};

function detectLanguage(className?: string): string {
  const match = /language-([\w+-]+)/i.exec(className || '');
  const raw = match?.[1] || '';
  const key = raw.toLowerCase();
  return LANG_ALIASES[key] || key || 'text';
}

const useStyles = makeStyles((theme: Theme) => ({
  messageContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  userMessage: {
    flexDirection: 'row-reverse',
  },
  assistantMessage: {
    flexDirection: 'row',
  },
  messageCard: {
    maxWidth: '70%',
    minWidth: '200px',
  },
  userCard: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  assistantCard: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.grey[800]
        : theme.palette.grey[100],
    color: theme.palette.text.primary,
  },
  systemCard: {
    backgroundColor:
      theme.palette.type === 'dark'
        ? theme.palette.warning.dark
        : theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  avatar: {
    width: theme.spacing(4),
    height: theme.spacing(4),
  },
  userAvatar: {
    backgroundColor: theme.palette.primary.main,
  },
  assistantAvatar: {
    backgroundColor: theme.palette.secondary.main,
  },
  systemAvatar: {
    backgroundColor: theme.palette.warning.main,
  },
  messageContent: {
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  timestamp: {
    fontSize: '0.75rem',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
    color: 'inherit',
  },
  messageActions: {
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
    opacity: 0.7,
    marginTop: theme.spacing(0.5),
  },
  copyButton: {
    padding: theme.spacing(0.5),
    fontSize: '0.875rem',
    color: 'inherit',
  },
  markdownContent: {
    '& pre': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.grey[900]
          : theme.palette.grey[100],
      padding: theme.spacing(1),
      borderRadius: theme.spacing(0.5),
      overflow: 'auto',
      fontSize: '0.875rem',
      fontFamily: 'monospace',
    },
    '& code': {
      backgroundColor:
        theme.palette.type === 'dark'
          ? theme.palette.grey[800]
          : theme.palette.grey[200],
      padding: theme.spacing(0.25, 0.5),
      borderRadius: theme.spacing(0.25),
      fontSize: '0.875rem',
      fontFamily: 'monospace',
    },
    '& blockquote': {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
      marginLeft: 0,
      paddingLeft: theme.spacing(1),
      fontStyle: 'italic',
      opacity: 0.8,
    },
    '& ul, & ol': {
      paddingLeft: theme.spacing(2),
    },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(0.5),
    },
    '& a': {
      color:
        theme.palette.type === 'dark' ? '#90caf9' : theme.palette.primary.main,
      textDecoration: 'underline',
      cursor: 'pointer',
      '&:hover': {
        color:
          theme.palette.type === 'dark'
            ? '#bbdefb'
            : theme.palette.primary.dark,
      },
    },
  },
  codeBlockContainer: {
    position: 'relative',
    marginBottom: theme.spacing(1),
    '&:hover $codeBlockCopyButton': {
      opacity: 1,
    },
  },
  codeBlockCopyButton: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    color:
      theme.palette.type === 'dark'
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
    opacity: 0,
    transition: 'opacity 0.2s ease',
    zIndex: 10,
  },
  progressStep: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  progressStepComplete: {
    color: theme.palette.text.disabled,
  },
}));

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
  timestamp?: Date;
  embeddings?: ResponseEmbedding[];
  agentToolCalls?: AgentToolCallTrace[];
  agentProgress?: string[];
  isStreamingProgress?: boolean;
}

interface ChatMessageProps {
  message: ChatMessage;
  viewMode?: 'markdown' | 'plaintext';
}

interface CodeBlockProps extends HTMLAttributes<HTMLPreElement> {
  className?: string;
  classes: {
    codeBlockContainer?: string;
    codeBlockCopyButton?: string;
  };
}

export const CodeBlock: FC<CodeBlockProps> = ({
  children,
  className,
  classes,
  ...props
}) => {
  const [copied, setCopied] = useState(false);
  const theme = useTheme();

  registerLanguages();

  const codeContent = String(children).replace(/\n$/, '');
  const language = detectLanguage(className);
  const isInline = !/\n/.test(codeContent);

  const handleCopyCode = async (e: MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(codeContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silent failure is acceptable - clipboard operations are non-critical
    }
  };

  if (isInline) {
    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  }

  const Highlighter = SyntaxHighlighter as any;

  return (
    <Box className={classes.codeBlockContainer}>
      <Highlighter
        language={language}
        style={theme.palette.type === 'dark' ? oneDark : oneLight}
        customStyle={{
          margin: 0,
          borderRadius: theme.spacing(0.5),
          fontSize: '0.875rem',
          fontFamily: 'monospace',
          padding: theme.spacing(1),
        }}
        {...props}
      >
        {codeContent}
      </Highlighter>
      <IconButton
        className={classes.codeBlockCopyButton}
        onClick={handleCopyCode}
        size="small"
        aria-label={copied ? 'Copied!' : 'Copy code'}
      >
        {copied ? (
          <CheckIcon fontSize="small" />
        ) : (
          <FileCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Box>
  );
};

export const ChatMessageComponent: FC<ChatMessageProps> = memo(
  ({ message, viewMode = 'markdown' }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [showCheckmark, setShowCheckmark] = useState(false);

    const getMessageStyles = () => {
      switch (message.role) {
        case MessageRole.USER:
          return {
            containerClass: classes.userMessage,
            cardClass: classes.userCard,
            avatarClass: classes.userAvatar,
            icon: <PersonIcon />,
          };
        case MessageRole.ASSISTANT:
          return {
            containerClass: classes.assistantMessage,
            cardClass: classes.assistantCard,
            avatarClass: classes.assistantAvatar,
            icon: <FaceIcon />,
          };
        case MessageRole.SYSTEM:
          return {
            containerClass: classes.assistantMessage,
            cardClass: classes.systemCard,
            avatarClass: classes.systemAvatar,
            icon: <FaceIcon />,
          };
        default:
          return {
            containerClass: classes.assistantMessage,
            cardClass: classes.assistantCard,
            avatarClass: classes.assistantAvatar,
            icon: <FaceIcon />,
          };
      }
    };

    const { containerClass, cardClass, avatarClass, icon } = getMessageStyles();

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(message.content);
        setShowCheckmark(true);
      } catch {
        // Silent failure is acceptable - clipboard operations are non-critical
      }
    };

    useEffect(() => {
      if (showCheckmark) {
        const timer = setTimeout(() => {
          setShowCheckmark(false);
        }, 1500);
        return () => clearTimeout(timer);
      }
      return undefined;
    }, [showCheckmark]);

    const markdownComponents: Components = {
      pre: ({ children }) => {
        return <>{children}</>;
      },
      a: ({ children, href, ...props }) => (
        <a
          {...props}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color:
              theme.palette.type === 'dark'
                ? '#90caf9'
                : theme.palette.primary.main,
          }}
        >
          {children}
        </a>
      ),
      code: ({
        inline,
        className,
        children,
        ...props
      }: HTMLAttributes<HTMLElement> & {
        inline?: boolean;
        className?: string;
      }) => {
        if (inline) {
          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        }
        return (
          <CodeBlock
            className={className}
            classes={{
              codeBlockContainer: classes.codeBlockContainer,
              codeBlockCopyButton: classes.codeBlockCopyButton,
            }}
            {...props}
          >
            {children}
          </CodeBlock>
        );
      },
      p: ({ children, ...props }) => (
        <p {...props}>{linkifyChildren(children)}</p>
      ),
      li: ({ children, ...props }) => (
        <li {...props}>{linkifyChildren(children)}</li>
      ),
      td: ({ children, ...props }) => (
        <td {...props}>{linkifyChildren(children)}</td>
      ),
    };

    const processedContent = useMemo(
      () => autoLinkUrls(message.content),
      [message.content],
    );

    const renderAgentProgress = () => {
      const steps = message.agentProgress;
      if (!steps || steps.length === 0) {
        return null;
      }

      return (
        <Box>
          {steps.map((step, index) => {
            const isLatest =
              message.isStreamingProgress && index === steps.length - 1;
            return (
              <Box
                key={`${step}-${index}`}
                className={`${classes.progressStep} ${
                  !isLatest ? classes.progressStepComplete : ''
                }`}
              >
                {isLatest ? (
                  <CircularProgress size={14} />
                ) : (
                  <CheckIcon fontSize="small" color="disabled" />
                )}
                <Typography variant="body2" component="span">
                  {step}
                </Typography>
              </Box>
            );
          })}
        </Box>
      );
    };

    const renderAnswer = () => {
      if (viewMode === 'markdown' && message.role === MessageRole.ASSISTANT) {
        return (
          <Box className={classes.markdownContent}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {processedContent}
            </ReactMarkdown>
          </Box>
        );
      }

      return (
        <Typography variant="body1" className={classes.messageContent}>
          {message.content}
        </Typography>
      );
    };

    const renderContent = () => {
      const hasAgentProgress =
        message.role === MessageRole.ASSISTANT &&
        !!message.agentProgress &&
        message.agentProgress.length > 0;

      // Streaming: progress only. Completed: keep steps, then render the answer.
      if (message.isStreamingProgress && hasAgentProgress) {
        return renderAgentProgress();
      }

      if (hasAgentProgress) {
        return (
          <Box>
            {renderAgentProgress()}
            <Box mt={1}>{renderAnswer()}</Box>
          </Box>
        );
      }

      return renderAnswer();
    };

    return (
      <Box className={`${classes.messageContainer} ${containerClass}`}>
        <Avatar className={`${classes.avatar} ${avatarClass}`}>{icon}</Avatar>
        <Card className={`${classes.messageCard} ${cardClass}`}>
          <CardContent>
            {renderContent()}

            {message.role === MessageRole.ASSISTANT &&
              message.embeddings &&
              message.embeddings.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6">Additional Information</Typography>
                  <EmbeddingsView embeddings={message.embeddings} />
                </Box>
              )}

            {message.role === MessageRole.ASSISTANT &&
              message.agentToolCalls &&
              message.agentToolCalls.length > 0 && (
                <Box mt={2}>
                  <Typography variant="h6">Additional Information</Typography>
                  <AgentSourcesView toolCalls={message.agentToolCalls} />
                </Box>
              )}

            <Box className={classes.messageActions}>
              {message.timestamp && (
                <Typography variant="caption" className={classes.timestamp}>
                  {message.timestamp.toLocaleTimeString()}
                </Typography>
              )}

              <Box style={{ flexGrow: 1 }} />

              <Tooltip title="Copy message">
                <IconButton
                  onClick={handleCopy}
                  className={classes.copyButton}
                  size="small"
                >
                  {showCheckmark ? (
                    <CheckIcon fontSize="small" />
                  ) : (
                    <FileCopyIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  },
);
